import { useState, useEffect } from "react";
import { createTeam } from "../../../../services/teams";
import { TextInput } from "../../../../components/TextInput";
import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import type { TeamResponse } from "../../../../types/api";
import "./AddTeamPanel.css";

interface AddTeamPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (team: TeamResponse) => void;
}

export const AddTeamPanel = ({ isOpen, onClose, onSuccess }: AddTeamPanelProps) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setNameError(undefined);
      setSaveError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isSaving, onClose]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Team name is required");
      return;
    }
    if (trimmed.length > 255) {
      setNameError("Team name must be 255 characters or fewer");
      return;
    }

    setNameError(undefined);
    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await createTeam(trimmed);
      if (!result.isNew) {
        setSaveError(`A team named "${result.name}" already exists.`);
        return;
      }
      const now = new Date().toISOString();
      onSuccess({ id: result.id, name: result.name, createdAt: now, updatedAt: now });
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to create team");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  return (
    <>
      <div
        className={`add-team-panel-overlay ${isOpen ? "add-team-panel-overlay--open" : ""}`}
        onClick={handleOverlayClick}
      />
      <div className={`add-team-panel ${isOpen ? "add-team-panel--open" : ""}`}>
        <div className="add-team-panel__content">
          <div className="add-team-panel__header">
            <h2 className="add-team-panel__title">Add Team</h2>
            <ModalCloseButton onClick={onClose} disabled={isSaving} />
          </div>

          <form
            className="add-team-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div>
              <label htmlFor="add-team-panel-team-name-0" className="add-team-panel__label">Team Name</label>
              <TextInput id="add-team-panel-team-name-0"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Team Name"
                error={nameError}
                disabled={isSaving}
              />
            </div>

            <div className="add-team-panel__footer">
              <Button variant="primary" type="submit" disabled={isSaving} fullWidth>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>

            {saveError && (
              <div className="add-team-panel__error">{saveError}</div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
